/**
 * Tabs, with the keyboard behaviour the pattern actually requires.
 *
 * Eight tablists had been hand-rolled across the screens, in three different
 * daisyUI variants, and exactly one implemented arrow-key navigation. A
 * `role="tab"` without it is a promise the markup does not keep: a screen
 * reader announces a tablist and the arrow keys then do nothing.
 *
 * @module components/ui/Tabs
 */

import React, { type ReactNode, useCallback, useRef } from "react";

import { Icon } from "./Icon";

export interface TabItem {
  key: string;
  label: string;
  icon?: string;
  /** A count or a status, after the label. */
  badge?: ReactNode;
  /** Locked: reachable and announced, but never opened. */
  disabled?: boolean;
  /** Why it is locked: a tooltip on hover and focus, and part of its name. */
  lockedReason?: string;
  "data-testid"?: string;
}

/**
 * Three tiers of tab. `underline` is the page's own strip and matches
 * `LinkTabs`; `lift` is a sub-section strip that reads as extending from the
 * selected tab above it; `box` switches a view or a record in place.
 */
export const TAB_VARIANTS = ["underline", "lift", "box"] as const;
export type TabVariant = (typeof TAB_VARIANTS)[number];

const LIST_CLASS: Readonly<Record<TabVariant, string>> = {
  underline: "flex flex-wrap items-center gap-1 border-b border-base-300",
  // The active tab takes the card's tone so the two read as one surface.
  lift: "tabs tabs-lift [--tab-bg:var(--color-base-200)]",
  box: "tabs tabs-box w-fit max-w-full",
};

const TAB_CLASS: Readonly<Record<TabVariant, string>> = {
  underline: "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium",
  lift: "tab gap-2",
  box: "tab gap-2",
};

function underlineState(selected: boolean): string {
  return selected
    ? // Weight is drawn with a stroke: a bolder face is wider, so selecting a tab
      // would reflow the strip.
      "border-primary text-base-content [-webkit-text-stroke:0.5px]"
    : "border-transparent text-muted hover:text-base-content";
}

export interface TabsProps {
  /** Which tier of tab this is. Defaults to the underline strip. */
  variant?: TabVariant;
  items: readonly TabItem[];
  value: string;
  onChange: (key: string) => void;
  /** Names the tablist, since the tabs alone do not say what they switch. */
  "aria-label": string;
  /** Prefixes the generated tab and panel ids. */
  idPrefix: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <Tabs items={tabs} value={tab} onChange={setTab} aria-label="Module sections" idPrefix="module" />
 * ```
 */
export function Tabs({
  variant = "underline",
  items,
  value,
  onChange,
  "aria-label": label,
  idPrefix,
  className = "",
  "data-testid": testId,
}: TabsProps): React.ReactElement {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  // The tab that holds the strip's single tab stop: the selected one, or the
  // first when nothing selected is on the strip. A locked tab stays focusable,
  // so it can hold the stop too.
  const stopKey = (items.find((item) => item.key === value) ?? items[0])?.key;

  const handleKey = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      // From the tab the key was pressed on, not from `value`: a parent may
      // decline a change (an unsaved-changes guard), leaving focus and
      // selection on different tabs, and computing from `value` then moves
      // from the wrong place every time.
      const focusedKey = event.currentTarget.dataset.tabKey;
      const focused = items.findIndex((item) => item.key === focusedKey);
      const index = focused === -1 ? items.findIndex((item) => item.key === value) : focused;
      const moveTo = (at: number): void => {
        const target = items[((at % items.length) + items.length) % items.length];
        if (!target) {
          return;
        }
        // Focus always follows the arrow; selection is reported only when it
        // changes, and never for a locked tab. They are separate because a
        // parent can decline a change, leaving focus and selection apart, and
        // arrowing back to the selected tab still has to move focus.
        refs.current.get(target.key)?.focus();
        if (target.key !== value && !target.disabled) {
          onChange(target.key);
        }
      };
      // One axis, because the strip declares one. Consuming the vertical
      // arrows too took the page's own scroll from a reader whose focus
      // happened to be on a tab.
      switch (event.key) {
        case "ArrowRight":
          event.preventDefault();
          moveTo(index + 1);
          break;
        case "ArrowLeft":
          event.preventDefault();
          moveTo(index - 1);
          break;
        case "Home":
          event.preventDefault();
          moveTo(0);
          break;
        case "End":
          event.preventDefault();
          moveTo(items.length - 1);
          break;
        default:
          break;
      }
    },
    [items, onChange, value],
  );

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
      className={`${LIST_CLASS[variant]} ${className}`.trim()}
      data-testid={testId}
    >
      {items.map((item) => {
        const selected = item.key === value;
        const locked = item.disabled && item.lockedReason ? item.lockedReason : undefined;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.key}`}
            aria-controls={`${idPrefix}-panel-${item.key}`}
            aria-selected={selected}
            // One stop for the whole tablist: Tab reaches the selected tab,
            // arrows move within. Landing on every tab in turn is what makes a
            // fourteen-tab strip unusable from the keyboard.
            tabIndex={item.key === stopKey ? 0 : -1}
            aria-disabled={item.disabled || undefined}
            ref={(node) => {
              if (node) {
                refs.current.set(item.key, node);
              } else {
                refs.current.delete(item.key);
              }
            }}
            onClick={() => {
              if (!item.disabled) {
                onChange(item.key);
              }
            }}
            // On the tab rather than the tablist: the tablist itself is not
            // focusable, so a handler there would only ever see bubbled events
            // and the linter is right to refuse it.
            onKeyDown={handleKey}
            // The tooltip lives on the tab, not a wrapper: daisyUI styles a tab
            // only as a direct child of `.tabs`. `inline-flex` restores the
            // display `.tooltip` overrides, and `tooltip-content` rather than
            // `data-tip` leaves the tab's own `::before` underline alone.
            className={[
              TAB_CLASS[variant],
              variant === "underline" ? underlineState(selected) : "",
              variant !== "underline" && selected ? "tab-active" : "",
              item.disabled ? "cursor-not-allowed text-muted-light" : "",
              locked ? "tooltip tooltip-bottom inline-flex" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-tab-key={item.key}
            data-testid={item["data-testid"]}
          >
            {item.disabled ? (
              <Icon name="lock-simple" aria-hidden />
            ) : (
              item.icon && <Icon name={item.icon} aria-hidden />
            )}
            {item.label}
            {item.badge}
            {locked ? (
              <>
                {" "}
                <span className="sr-only">(locked: {locked})</span>
                {/* daisyUI opens a tooltip on a focused descendant only; the
                    tab is the focused element, so its own focus opens it. */}
                <span className="tooltip-content in-focus-visible:opacity-100" aria-hidden="true">
                  {locked}
                </span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  /** The key of the tab this panel belongs to. */
  tabKey: string;
  idPrefix: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}

/** The region a tab controls, tied back to it for a screen reader. */
export function TabPanel({
  tabKey,
  idPrefix,
  active,
  children,
  className = "",
}: TabPanelProps): React.ReactElement {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${tabKey}`}
      aria-labelledby={`${idPrefix}-tab-${tabKey}`}
      hidden={!active}
      tabIndex={0}
      className={className || undefined}
    >
      {active ? children : null}
    </div>
  );
}

export interface TabCardProps {
  children: ReactNode;
  /** Sits under the panel, inside the card: the save row of a form. */
  footer?: ReactNode;
  "data-testid"?: string;
}

/**
 * The card a `lift` strip hangs off. Place it directly under the strip, in
 * the same container, so both take one width; its top-left corner is square
 * where the selected tab joins it.
 */
export function TabCard({
  children,
  footer = undefined,
  "data-testid": testId,
}: TabCardProps): React.ReactElement {
  return (
    <div
      className="rounded-box rounded-tl-none border border-base-300 bg-base-200 p-6 max-sm:p-4"
      data-testid={testId}
    >
      {children}
      {footer ? <div className="mt-6 border-t border-base-300 pt-4">{footer}</div> : null}
    </div>
  );
}
